/* =========================================================================
 * A GOVERNING BODY → THE CHAMPIONSHIPS IT ACTUALLY CROWNS.
 *
 * Brian: "in the GOVERNING BODIES tab — i should be able to see how many
 * championships there are and the interconnectedness — we should be able to
 * click on them and see when they are and who they are with then in the
 * command center."
 *
 * ⚠️ THE PAGE WAS HALF A DIRECTORY. High-school cards carried record counts,
 * matched by STATE NAME — but a COLLEGE conference has no state, so every ACC,
 * Big Ten and NCAA card showed nothing at all. The college half of the board,
 * which is where a large share of ring orders sit, was decoration: a name, a
 * logo slot and an outbound link. That is the gap this closes.
 *
 * Pure, and it takes the universe as an argument. The Command Center already
 * holds every championship in memory, so relating a body to its titles costs
 * one pass and no request.
 * ========================================================================= */

import { STATES, PROVINCES } from './usStates.js'

const S = (v) => String(v ?? '').trim()
const L = (v) => S(v).toLowerCase()

/* The places a card can actually be filed under. Read from the state and
   province tables rather than hand-listed, so a new province cannot desync it.
   See the home-state note in matcherFor for why this exists. */
const IS_GEOGRAPHY = new Set([
  ...STATES.map((s) => L(s.name)),
  ...PROVINCES.map((p) => L(p.name)),
])

/* =========================================================================
 * ⚠️ A ZERO ON A CARD IS TWO COMPLETELY DIFFERENT PROBLEMS.
 *
 * Brian: "There are still governing bodies that show zero championships which
 * is unacceptable." He is right, but the two zeros need OPPOSITE fixes and a
 * card that shows a bare 0 cannot tell you which one you are looking at:
 *
 *   NOT HELD    — no record anywhere names this body. ASUN Conference is this:
 *                 zero in `sr_conference`, zero in any title. That is a real
 *                 acquisition gap and the answer is to go and harvest them.
 *
 *   NOT MATCHED — records exist and the card cannot see them. NCAA was this,
 *                 275 records deep, and a rep reading that card would have
 *                 concluded we hold no NCAA titles and stopped.
 *
 *   DEFECTIVE   — records exist, name this body, and are not championships.
 *                 25 records have `championship_name` equal to the literal
 *                 string "NEC" with no event on them at all, parked at
 *                 `sr_conference = 'Unspecified'`. Counting those as 25 NEC
 *                 championships would be worse than the zero it replaced: a
 *                 fabricated number a rep would quote. They are surfaced and
 *                 flagged, never counted as held.
 * ========================================================================= */

/**
 * ⚠️ A NAME THAT IS ONLY AN ACRONYM IS NOT A CHAMPIONSHIP NAME.
 * "NEC" names no event, no sport, no division and no year. Whatever these
 * records were meant to be, they were truncated on the way in.
 */
export function isDefectiveName(champ) {
  const r = champ?.raw || champ || {}
  const nm = S(champ?.name ?? r.championship_name)
  if (!nm) return true
  /* One token, no lower-case letters, five characters or fewer: an acronym
     sitting alone in the name field. Deliberately narrow — "NCAA Division I
     FCS Football Championship" and "The MAJORS" both pass unharmed. */
  return /^[A-Z0-9&.-]{1,5}$/.test(nm)
}

/**
 * ⚠️ TWO LEVELS MATCH ON TWO DIFFERENT FIELDS, and that is not a shortcut.
 *
 * A high-school body owns a STATE — every AHSAA title is an Alabama title, so
 * the state is the join. A conference owns a NAME — the ACC's titles are spread
 * across fifteen states and the state field says nothing about who sanctioned
 * them. Using one rule for both is what left the college cards empty.
 *
 * `sr_conference` is the join for a conference. It is a GOVERNING-BODY field,
 * not a "this is a conference championship" flag — on a high-school record it
 * holds the state association — which is exactly why it works here for both.
 */
/**
 * ⚠️ A BODY IS NOT ITS STATE. THIS WAS A REAL DEFECT.
 *
 * Brian: "if i select nchsaa - i feel like it is just pulling every
 * championship from the state. not just nchsaa - if we just want to show a
 * state - that should be a selection."
 *
 * He is right, and the first version of this file was wrong in exactly that
 * way: it matched a high-school body by STATE NAME, so opening NCHSAA showed
 * every North Carolina championship — NCHSAA's public-school titles AND
 * NCISAA's independent-school titles, as one undifferentiated list. Those are
 * two different associations, two different sets of member schools, and two
 * different sales conversations. The count on the card was the state's, not the
 * body's, which made the number actively misleading rather than merely broad.
 *
 * The reason it looked plausible is that 34 states have exactly one association
 * on our board, so state and body coincide there. They diverge precisely where
 * it matters most — the states with a private/independent association beside
 * the public one.
 *
 * The fix is that a body matches on ITSELF. `sr_conference` carries the
 * association on a high-school record ("North Carolina High School Athletic
 * Association" on 188 of them), so the same join works at both levels; the
 * name is also checked, because plenty of records lead with the acronym
 * ("NCHSAA Football 4A") without filling the field.
 *
 * `scope: 'state'` is the selection Brian asked for — the whole state, every
 * association in it — and it is now a deliberate choice rather than the silent
 * default.
 */
export function matcherFor(entity, { scope = 'body' } = {}) {
  if (!entity) return () => false

  /* ⚠️ A ROLL-UP MATCHES NOTHING ON ITS OWN. It is a heading over the real
     bodies beside it, so its number has to BE theirs — see rollupFor(). Letting
     it match would give the tab two answers to one question, which is exactly
     how the SPIRIT card came to read 1,454 against Varsity Spirit's 1,355. */
  if (entity.rollup) return () => false

  if (scope === 'state') {
    const state = L(entity.state)
    if (!state) return () => false
    return (c) => {
      const r = c?.raw || c || {}
      return L(r.sr_state_location) === state || L(r.sr_state) === state || L(c?.state) === state
    }
  }

  /* A body is named several ways across 14k records — "Big Ten Conference",
   * "Big Ten", "B1G"; "North Carolina High School Athletic Association",
   * "NCHSAA". Match the full name, then the acronym as a WHOLE WORD.
   *
   * ⚠️ WHOLE WORD, NOT SUBSTRING. "NC" lives inside "NCHSAA" and that exact
   * trap has already cost this project once — a state qualifier silently
   * swallowed by a body acronym, with a test that agreed with the bug. An
   * acronym of two or three letters makes it a certainty, not an edge case.
   */
  const name = L(entity.name)
  const acr = S(entity.acronym)
  const acrEsc = acr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const acrRe = acr.length >= 2 ? new RegExp(`\\b${acrEsc}\\b`, 'i') : null
  /* The title-only regex: case-SENSITIVE, and only for a genuinely all-caps
     acronym. See the case note at the bottom of this matcher for why. */
  const acrTitleRe = acr.length >= 2 && acr === acr.toUpperCase() && /[A-Z]/.test(acr)
    ? new RegExp(`\\b${acrEsc}\\b`)
    : null

  /**
   * ⚠️ AN ACRONYM IS NOT A KEY, AND WHOLE-WORD MATCHING DID NOT SAVE IT.
   *
   * Brian's capture, 2026-09-19: Arkansas Activities Association reads 318
   * championships. The object holds at most 176 on any Arkansas key. Idaho
   * reads 184 against 103 in the entire object.
   *
   * The comment above congratulated itself for matching the acronym as a WHOLE
   * WORD rather than a substring, and that was the wrong worry. Two collisions
   * survive whole-word matching and they are the two cards that are wrong:
   *
   *   AAA   is Arkansas Activities Association — and it is also a
   *         CLASSIFICATION used by many states. "Class AAA Football",
   *         "Tennessee AAA Basketball". `\bAAA\b` matches every one of them,
   *         which is where 318 comes from.
   *   IHSAA is Idaho High School Activities Association — and ALSO Indiana
   *         High School Athletic Association, and ALSO Iowa. Three states,
   *         one acronym, one card counting all three.
   *
   * So the acronym is a WEAK signal and must be qualified by the state. The
   * full association NAME stays a STRONG signal: "Idaho High School Activities
   * Association" names exactly one body and needs no help.
   *
   * This does NOT reinstate matching by state — that was the earlier defect,
   * where NCHSAA showed NCISAA's titles too. The state is an ADDITIONAL
   * condition on a weak match, never a substitute for the body signal, so two
   * associations in one state stay separate.
   */
  /**
   * ⚠️ A HOME STATE HAS TO BE A PLACE. THIS IS THE DEFECT THAT EMPTIED THE
   * COLLEGE BOARD, AND IT WAS INTRODUCED BY THE FIX ABOVE.
   *
   * Brian, with a screenshot: "NCAA has zero championships listed even though
   * we know there are 90+ championship opportunities… There are still
   * governing bodies that show zero championships which is unacceptable."
   *
   * The object holds 275 records at `sr_conference = 'NCAA'`. The card read 0.
   * That is not a data gap, it is this matcher.
   *
   * `entity.state` is the GROUP a card is filed under, and only on the
   * high-school tab is that group a geography. On the college tab the groups
   * are "NCAA", "NAIA", "NJCAA", "NCCAA", "USCAA"; on cheer it is "Cheer and
   * Dance"; on professional it is a league family. So the state qualifier
   * added to stop `\bAAA\b` matching "Class AAA Football" — which required a
   * weak acronym match to AGREE on the state — started demanding that an NCAA
   * record carry `sr_state_location = 'NCAA'`. No record does, and none ever
   * will: the 64 values in that field are places. Every college card whose
   * only signal is its acronym therefore evaluated to false, silently.
   *
   * Twelve cards read zero because of it: NCAA, NAIA, NJCAA, NCCAA, USCAA and
   * seven conferences. The guard was right; its input was not.
   *
   * So the qualifier now fires only when the group really is a place, checked
   * against the state and province tables rather than against the truthiness
   * of a string. "Arkansas" qualifies and "Class AAA Football" still cannot
   * reach the Arkansas Activities Association card; "NCAA" does not qualify,
   * and the NCAA card can see its own records again.
   */
  const homeState = IS_GEOGRAPHY.has(L(entity.state)) ? L(entity.state) : ''
  const stateOf = (r, c) => L(r.sr_state_location) || L(r.sr_state) || L(c?.state)

  /**
   * ⚠️ ONE ORGANISATION, SEVERAL LABELS IN THE OBJECT — and merging the records
   * is NOT this module's business.
   *
   * The object holds "USASF" on 52 records and "U.S. All Star Federation
   * (USASF)" on 2; "Varsity Spirit" on 1,354 and "Varsity Spirit — The Quest"
   * on 1. Those are the same bodies written down two ways. Collapsing them in
   * the DATA is a record merge and needs a data-repair permission; until then
   * the alternate label has to reach the right card or the records are
   * invisible for a reason that is purely clerical.
   *
   * `aliases` is that list. It is a STRONG signal and deliberately narrower
   * than the acronym rule above:
   *
   *   · it reads ONLY `sr_conference`, the governing-body field. A title that
   *     merely MENTIONS a body is not that body's record — one TSSAA
   *     championship is named "…Varsity Spirit…" and matching titles would
   *     have put a TSSAA record on the Varsity Spirit card.
   *   · it requires three characters, so it can never become the two-letter
   *     substring trap that "AAA" and "NC" have already cost this project.
   *   · it is never qualified by state, because a cheer or all-star body has
   *     no home state at all — which is exactly why the acronym path, which
   *     falls back to the state, cannot serve these bodies.
   *
   * An alias is a label VERIFIED against the object by an operator, not a
   * guess — which is why this is a list on the registry entry rather than a
   * normalizer trying to be clever about names. Every previous name-matching
   * build in this repo failed by collapsing distinct real organisations into
   * one key; an explicit list cannot do that silently.
   *
   * ⚠️ NO EXISTING ENTITY CARRIES `aliases`, so this is a NO-OP for the high
   * school, college, Canada and professional tabs. The control set was
   * measured before and after: GHSA 181, UIL 213, VHSL 192, KHSAA 59.
   */
  const aliases = (Array.isArray(entity.aliases) ? entity.aliases : [])
    .map(L).filter((a) => a.length >= 3)

  return (c) => {
    const r = c?.raw || c || {}
    const conf = S(r.sr_conference ?? c?.conference)

    /* STRONG: the conference field names the association in full. */
    if (conf) {
      const lc = L(conf)
      if (name && (lc === name || lc.includes(name))) return true
      for (const a of aliases) if (lc === a || lc.includes(a)) return true
    }

    /* STRONG: the title itself carries the full association name. */
    const nm = S(c?.name ?? r.championship_name)
    if (name && nm && L(nm).includes(name)) return true

    /* WEAK: acronym only. Allowed for a body with no home state (a conference
     * spans many), but for a state association the record must agree on the
     * state. A record with NO state cannot clear a weak match, because that is
     * precisely the hole "Class AAA" fell through. */
    /* ⚠️ A BODY WITH NO HOME STATE HAS NO QUALIFIER, SO IT GETS A NARROWER
     * SIGNAL INSTEAD — AND THIS GUARD CAUGHT ME REOPENING THE COLLISION.
     *
     * Narrowing `homeState` to real geography (above) fixed the NCAA card and
     * immediately broke `verify-cheer-bodies.mjs`, which expects exactly five
     * cross-tab overlaps and found seven. The cause: the cheer parent card's
     * acronym is the word "SPIRIT". Its group is "Cheer and Dance", which is
     * not a place, so it lost its qualifier — and `\bSPIRIT\b` then matched 55
     * STATE championships by title: "UIL Spirit / Cheer 6A DI", "CHSAA Spirit
     * — Pom Girls 5A", "KSHSAA Game Day Spirit — Dance 4A", "NHIAA Spirit
     * (Winter)". Those are UIL, CHSAA, KSHSAA and NHIAA titles. Putting them
     * on a cheer card is the precise failure this whole file was rewritten to
     * stop, arriving from a new direction.
     *
     *   NCAA   sr_conference = 'NCAA'  → 275, the card works again
     *   SPIRIT sr_conference = 'UIL'   → no match, the state keeps its title
     *
     * The rule that separates them is in the note below.
     */
    if (!acrRe) return false

    /* ⚠️ AN ACRONYM IS WRITTEN IN CAPITALS. A WORD IN TITLE CASE IS A WORD.
     *
     * This is the discriminator, and it took two wrong answers to find. The
     * title path cannot simply be removed for stateless bodies: doing that
     * took the South Atlantic Conference from 136 records to nothing and the
     * professional tab from 95 to 28, because plenty of real conferences are
     * named only in the title. Trading an over-match for a silent under-match
     * is not a fix, it is the same failure wearing the other coat.
     *
     * What actually separates the good match from the bad one is CASE.
     *   "SAC Men's Basketball Championship"  → SAC, capitals, an acronym.
     *   "UIL Spirit / Cheer 6A DI"           → Spirit, title case, a word.
     * So the title is searched case-SENSITIVELY, and only for an acronym that
     * is genuinely all-caps. `\bSPIRIT\b` stops matching 55 UIL, CHSAA,
     * KSHSAA, WHSAA, NHIAA and NMAA state titles, and SAC keeps its 136.
     *
     * A mixed-case label like "Varsity Spirit" is not an acronym at all, so it
     * gets no title path whatsoever — which is also what stops the TSSAA
     * record named "…Coed Varsity Spirit Program" landing on the Varsity
     * Spirit card. That body matches on `sr_conference` through its verified
     * alias, which is the strong signal it should have been using all along.
     *
     * The conference field stays case-insensitive throughout: it holds a
     * governing body by definition, so there is no word/acronym ambiguity to
     * resolve there. */
    const byConf = Boolean(conf && acrRe.test(conf))

    /* ⚠️ IF THE RECORD ALREADY NAMES ITS GOVERNING BODY, THE TITLE IS NOT A
     * VOTE. The same guard caught this one too, at six overlaps instead of
     * five: "STUNT National Championship — NAIA" carries
     * `sr_conference = 'USA Cheer (STUNT)'`, and `\bNAIA\b` in the title put
     * it on the NAIA card as well. USA Cheer sanctions it; NAIA is where it is
     * held. Two cards claiming one title is how a board starts double-counting
     * what it crowns.
     *
     * So the title is a FALLBACK, consulted only when the governing-body field
     * is empty or says "Unspecified" — which is exactly the population it was
     * added for, including the 25 truncated NEC records. A populated
     * `sr_conference` is the answer and the title does not get to override it.
     */
    const bodyStated = conf && conf !== 'Unspecified'
    const byTitle = !bodyStated && Boolean(acrTitleRe && nm && acrTitleRe.test(nm))

    if (!homeState) return byConf || byTitle
    if (!byConf && !byTitle) return false
    return stateOf(r, c) === homeState
  }
}

/**
 * Everything a body crowns, plus the numbers that make the card worth looking
 * at rather than a name with a logo beside it.
 *
 * ⚠️ THE COUNTS DESCRIBE THE WORK, NOT THE VOLUME. "28 records" is trivia.
 * "9 crowned, 4 with a date, 15 needing one" is a worklist — which is the
 * difference Brian has asked for twice now, first on the Integrity page and now
 * here. A number you cannot act on is a number that teaches people to stop
 * looking.
 */
export function championshipsFor(entity, universe = [], { scope = 'body' } = {}) {
  const match = matcherFor(entity, { scope })
  const rows = []
  for (const c of universe) if (match(c)) rows.push(c)
  return summariseRows(rows)
}

/* One place that turns a set of rows into the numbers on a card, so a roll-up
   and a body card can never describe the same rows differently. */
export function summariseRows(rows = []) {
  let crowned = 0, dated = 0, ambiguous = 0, defective = 0
  const sports = new Set()
  for (const c of rows) if (isDefectiveName(c)) defective += 1
  for (const c of rows) {
    const r = c?.raw || c || {}
    if (S(c?.champion) || S(r.current_champion)) crowned += 1
    if (c?.effDate || S(r.sr_event_date)) dated += 1
    if (S(r.sr_sport)) sports.add(S(r.sr_sport))
    const lvl = S(r.sr_level); const seg = S(r.sr_segment)
    if ((lvl === 'Conference' || seg === 'Conference')
      && !/regular[\s-]?season|tournament|championship game/i.test(S(c?.name ?? r.championship_name))) ambiguous += 1
  }

  return {
    rows,
    total: rows.length,
    crowned,
    dated,
    /* Stated as the gap rather than the coverage: "15 need a date" is a task,
     * "13 dated" is a statistic. */
    undated: rows.length - dated,
    uncrowned: rows.length - crowned,
    sports: sports.size,
    ambiguous,
    /* Surfaced and named, never folded into `total` as if they were titles. */
    defective,
    /* What we can actually claim to hold. */
    held: rows.length - defective,
  }
}

/**
 * WHICH KIND OF ZERO THIS CARD IS SHOWING — in words a rep can act on.
 *
 * ⚠️ AN UNEXPLAINED ZERO IS THE SAME FAILURE AS AN INVISIBLE RECORD, reached
 * from the other side: one hides a record that exists, the other invents an
 * absence that is not real. Both end with a rep concluding there is nothing
 * here and moving on. So no card is allowed to show a bare 0.
 *
 * Returns null when the card is not at zero — there is nothing to explain.
 */
export function zeroKind(entity, universe = [], { scope = 'body' } = {}) {
  const { total, defective } = championshipsFor(entity, universe, { scope })
  if (total - defective > 0) return null

  if (defective > 0) {
    return {
      kind: 'defective',
      label: `${defective} record${defective === 1 ? '' : 's'} name this body but ${defective === 1 ? 'is' : 'are'} not a championship`,
      detail: 'The name field holds only an acronym — no event, sport, division or year. These are truncated records, not titles, so they are not counted as held. Repairing them is a data fix.',
    }
  }

  /* ⚠️ THE DISTINCTION IS MEASURED, NOT ASSUMED. We ask the object whether
     anything anywhere mentions this body before calling it an acquisition gap,
     because "the card cannot match it" and "we do not have it" look identical
     from the card and need opposite work. */
  const acr = S(entity?.acronym)
  const nm = L(entity?.name)
  const acrRe = acr.length >= 3
    ? new RegExp(`\\b${acr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i')
    : null
  const mentioned = universe.some((c) => {
    const r = c?.raw || c || {}
    const hay = `${S(r.sr_conference)} ${S(c?.name ?? r.championship_name)}`
    if (nm && L(hay).includes(nm)) return true
    return Boolean(acrRe && acrRe.test(hay))
  })

  if (mentioned) {
    return {
      kind: 'unmatched',
      label: 'records mention this body but this card cannot match them',
      detail: 'This is a matcher fault, not an acquisition gap — the records exist. Report it rather than harvesting duplicates.',
    }
  }

  return {
    kind: 'not-held',
    label: 'we hold no championships for this body yet',
    detail: 'Nothing in the object names it, in the governing-body field or in any title. This is a real gap and the answer is to go and acquire them.',
  }
}

/* =========================================================================
 * ⚠️ A THIN BODY MUST LOOK THIN. A CARD IS NOT ALLOWED TO PRESENT AN OBVIOUSLY
 * INCOMPLETE CONFERENCE AS COMPLETE.
 *
 * Brian hit this live: the Pac-12 held SIX championships next to the SEC's 42,
 * the Big Ten's 36, the Ivy League's 33 and the ACC's 31. Its real 2026-27 slate
 * is nineteen. Nothing on the card said six was wrong — six rendered exactly
 * the way forty-two renders, in the same type, with the same "See what they
 * crown" link under it. `zeroKind` above already refuses to let a card show a
 * bare 0, and that rule stopped precisely one short of the case that actually
 * embarrassed him: a SMALL NON-ZERO is the same lie with better manners.
 *
 * ⚠️ AND THE COMPARISON HAS TO BE AGAINST LIKE FOR LIKE, OR IT CRIES WOLF.
 * A first version of this pooled every high-school body into one median of 93
 * and flagged VISAA, the Georgia Independent Athletic Association and the
 * Canyon Athletic Association as thin. Those are private and independent
 * associations that crown far fewer titles than a public state association BY
 * NATURE. Six false positives out of thirteen — and a flag that is wrong half
 * the time gets ignored, which leaves the board exactly where it started. So a
 * body is only ever measured against bodies of its own KIND, and a class with
 * too few members to have a meaningful median produces no signal at all.
 * ========================================================================= */

/** MIN_PEERS — below this a "median" is one or two bodies and means nothing. */
const MIN_PEERS = 4
/** A body under half its class median is thin. Deliberately blunt: the signal
 *  says "go and check", not "here is the exact shortfall". */
const THIN_RATIO = 0.5

/**
 * Which bodies this one should be compared to. Read off the registry entry
 * rather than guessed from its records, because a body's KIND is a fact about
 * the organisation and its record count is the thing under test — deriving the
 * class from the data would let a thin body argue itself into its own peer group.
 */
export function coverageClassOf(entity) {
  if (!entity || entity.rollup) return null
  const lvl = S(entity.level)
  if (lvl === 'National') return 'college-national'
  const kind = S(entity.kind)
  if (kind === 'conference') return `college-conference-d${S(entity.division) || 'I'}`
  if (kind === 'league') return 'pro-league'
  /* A high-school entity: `primary` marks the state's public association, and
   * everything else is an independent, private, city or homeschool body. */
  if (entity.primary) return 'hs-public'
  return `hs-${kind || 'independent'}`
}

/**
 * The peer baseline, MEASURED from the same universe the cards render, never
 * hard-coded. A harvest that doubles a class moves its median with no edit here.
 *
 * Returns { byClass: { [cls]: { median, peers } } }. Compute once per tab and
 * pass it to `coverageSignal` for each card — it is one pass over the universe
 * per body, so doing it per card per render is how a board gets slow.
 */
export function peerBaseline(entities = [], universe = [], opts = {}) {
  const buckets = new Map()
  for (const e of entities) {
    const cls = coverageClassOf(e)
    if (!cls) continue
    const { held } = championshipsFor(e, universe, opts)
    /* ⚠️ ZERO-HOLDING BODIES ARE EXCLUDED FROM THE MEDIAN. They are an
     * acquisition gap that `zeroKind` already reports, and leaving them in drags
     * the baseline down until a thin body looks average — the flag would quietly
     * switch itself off exactly as coverage got worse. */
    if (held <= 0) continue
    if (!buckets.has(cls)) buckets.set(cls, [])
    buckets.get(cls).push(held)
  }
  const byClass = {}
  for (const [cls, ns] of buckets) {
    ns.sort((a, b) => a - b)
    byClass[cls] = { median: ns[Math.floor(ns.length / 2)], peers: ns.length, max: ns[ns.length - 1] }
  }
  return { byClass }
}

/**
 * IS THIS CARD THINNER THAN IT LOOKS? Returns null when there is nothing to say.
 *
 * ⚠️ IT NEVER FIRES ON A ZERO. `zeroKind` owns that case and says which of the
 * three kinds of zero it is. Two signals describing one condition is how a board
 * starts giving two answers to one question — the same mistake as the SPIRIT
 * roll-up counting 1,454 against Varsity Spirit's 1,355.
 */
export function coverageSignal(entity, universe = [], baseline = null, opts = {}) {
  if (!entity || entity.rollup) return null
  const cls = coverageClassOf(entity)
  if (!cls) return null
  const base = baseline?.byClass?.[cls]
  if (!base || base.peers < MIN_PEERS || base.median < MIN_PEERS) return null

  const { held, crowned, undated } = championshipsFor(entity, universe, opts)
  if (held <= 0) return null                         // zeroKind's job, not this one
  if (held >= base.median * THIN_RATIO) return null  // nothing to report

  const short = base.median - held
  return {
    kind: 'thin',
    held,
    median: base.median,
    peers: base.peers,
    short,
    cls,
    label: `${held} championship${held === 1 ? '' : 's'} against a median of ${base.median} for the ${base.peers} comparable bodies we hold`,
    detail: `This card is not complete and should not read as though it is. Roughly ${short} more titles would put it level with its peers — that is an ESTIMATE from what we hold elsewhere, not a researched slate, so the next step is to establish what this body actually crowns from its own championship schedule and create only the difference.`,
    crowned,
    undated,
  }
}

/**
 * The deep link into the Command Center for this body.
 *
 * ⚠️ IT CARRIES A FILTER, NOT JUST A ROUTE. Landing a rep on an unfiltered
 * 14,279-record board and expecting them to re-find the ACC is not a link, it
 * is a dismissal — and it is the reason this page's cards previously pointed
 * OUT to theacc.com rather than in to our own data.
 */
export function commandCenterHref(entity, { scope = 'body' } = {}) {
  if (!entity) return '/command-center'
  const p = new URLSearchParams()
  /* ⚠️ THE LINK CARRIES THE SAME SCOPE THE DRAWER IS SHOWING. Sending a rep to
   * a state-filtered board after they opened one association is the same
   * conflation, one screen later. */
  if (scope === 'state') p.set('state', S(entity.state))
  else p.set('conference', S(entity.name) || S(entity.acronym))
  p.set('view', 'gaps')
  return `/command-center?${p.toString()}`
}

/** Sort for the drill-in: soonest first, undated last — never 1970. */
export function bySoonest(a, b) {
  const da = a?.effDate ? new Date(a.effDate).getTime() : null
  const db = b?.effDate ? new Date(b.effDate).getTime() : null
  if (da && db) return da - db
  if (da) return -1
  if (db) return 1
  return S(a?.name).localeCompare(S(b?.name))
}


/**
 * THE ROLL-UP CARD'S NUMBER IS ITS CHILDREN'S, DEDUPLICATED.
 *
 * ⚠️ DEDUPLICATED IS THE LOAD-BEARING WORD. Summing the child totals would
 * double-count any record two bodies both claim, and this tab has had exactly
 * that (the five WPIAL records). A union over record identity cannot.
 */
export function rollupFor(entity, siblings = [], universe = [], opts = {}) {
  const others = siblings.filter((e) => e && e !== entity && !e.rollup)
  const matchers = others.map((e) => matcherFor(e, opts))
  const seen = new Set()
  const rows = []
  for (const c of universe) {
    const id = String(c?.id || c?.raw?.id || c?.hs_object_id || c?.raw?.hs_object_id || '')
    if (seen.has(id)) continue
    if (!matchers.some((m) => m(c))) continue
    seen.add(id)
    rows.push(c)
  }
  return summariseRows(rows)
}

/**
 * ⚠️ A CONFERENCE VALUE THAT IS ANOTHER CONFERENCE VALUE PLUS A DASH SUFFIX IS
 * AN EVENT, NOT A BODY.
 *
 * "Varsity Spirit — The Quest" is one record. The Quest is something Varsity
 * Spirit RUNS; it does not sanction anything on its own. Carried as a second
 * governing-body value it becomes a second card with a count of one, and the
 * parent's count silently disagrees with its own written note by exactly that
 * one record — which is the discrepancy Brian spotted.
 *
 * This finds the pattern rather than that one instance, because it will recur:
 * every acquisition run that reads an event name into the body field produces
 * another. Reported, never auto-merged — folding one is a CHANGE to a
 * populated field and needs `cc_data_repair`.
 */
export function eventSuffixedBodies(universe = []) {
  const counts = new Map()
  for (const c of universe) {
    const v = S((c?.raw || c || {}).sr_conference)
    if (v) counts.set(v, (counts.get(v) || 0) + 1)
  }
  const out = []
  for (const value of counts.keys()) {
    const m = value.match(/^(.*?)\s*[\u2014\u2013-]\s*(.+)$/)
    if (!m) continue
    const parent = m[1].trim()
    if (!parent || !counts.has(parent)) continue
    out.push({ value, count: counts.get(value), parent, parentCount: counts.get(parent), event: m[2].trim() })
  }
  return out.sort((x, y) => y.count - x.count)
}
